import React, { useEffect, useMemo, useState } from 'react';
import styles from './styles.module.css';
import parts from '../moduleCard/moduleParts.module.css';
import { FormControl, InputLabel, MenuItem, Select, ToggleButton, ToggleButtonGroup } from '@mui/material';
import IconAdd from '@mui/icons-material/Add';
import IconEdit from '@mui/icons-material/Edit';
import AccessibilityNewOutlinedIcon from '@mui/icons-material/AccessibilityNewOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import CustomButton from '../../../../components/buttons/CustomButton';
import ModuleCard from '../moduleCard/ModuleCard';
import ModuleEmptyState from '../moduleCard/ModuleEmptyState';
import DateChip from '../moduleCard/DateChip';
import CreateBodyMeasurementsPopup from '../../../../popup/CreateBodyMeasurementsPopup/CreateBodyMeasurementsPopup';
import BodyMeasurementHistoryPopup from '../../../../popup/BodyMeasurementHistoryPopup/BodyMeasurementHistoryPopup';
import BodyOverview from './BodyOverview';
import BodyExampleOverview from './BodyExampleOverview';
import BodyMeasurementsTable from './BodyMeasurementsTable';
import HiddenReport from '../../../../components/documents-templates/report/HiddenReport';
import useReportExport from '../../../../components/documents-templates/report/useReportExport';
import { buildBodyMeasurementsJob } from '../../modules/reportSections';
import BodyMeasurementsReportTemplate from '../../../../components/documents-templates/BodyMeasurementsReportTemplate';
import { variantFromGender } from '../../../../components/bodySilhouette/bodySilhouetteFigures';
import { REFERENCE_FIRST, REFERENCE_PREVIOUS, findReference, formatDate, sortByDateDesc } from './bodyMeasurementsMath';

const VIEW_OVERVIEW = 'overview';
const VIEW_TABLE = 'table';

/**
 * Módulo "Medidas corporais": avaliações do paciente (peso, altura, gordura,
 * circunferências, dobras e diâmetros) com visão geral na silhueta, indicadores
 * calculados (IMC classificado, % de gordura pelas dobras, relações
 * cintura-quadril e cintura-estatura) e histórico em tabela.
 *
 * `data`: avaliações do paciente (todas, de qualquer atendimento).
 * `gender`: sexo do cadastro, escolhe a silhueta e as equações por sexo.
 * `birthday`: nascimento do cadastro, dá a idade na data de cada avaliação.
 * `enabled`: false na aba Saúde do paciente (só leitura, mas ainda exporta).
 * `patientName`, `professionalId`, `companyConfig`: identificação no PDF.
 */
const BodyMeasurements = ({
  data,
  patientId,
  appointmentId,
  gender,
  birthday,
  enabled,
  collapsible = false,
  onUpdated,
  patientName,
  professionalId,
  companyConfig,
}) => {
  const [history, setHistory] = useState(Array.isArray(data) ? data : []);
  const [view, setView] = useState(VIEW_OVERVIEW);
  const [referenceMode, setReferenceMode] = useState(REFERENCE_PREVIOUS);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [historyKey, setHistoryKey] = useState(null);

  useEffect(() => {
    setHistory(Array.isArray(data) ? data : []);
  }, [data]);

  const sorted = useMemo(() => sortByDateDesc(history), [history]);
  const current = sorted[0] || null;
  const reference = current ? findReference(history, current, referenceMode) : null;
  const ownEntry = appointmentId
    ? history.find((item) => String(item.appointmentId) === String(appointmentId)) || null
    : null;
  const variant = variantFromGender(gender);

  const report = useReportExport({
    patientName,
    professionalId,
    companyConfig,
    reportType: 'medidas-corporais',
    prepare: () => buildBodyMeasurementsJob(history, current, reference, variant, birthday),
  });

  function popupHideHandler(needsReload) {
    setShowPopup(false);
    setSelectedEntry(null);
    if (needsReload && onUpdated) onUpdated();
  }

  function openPopup(entry) {
    setSelectedEntry(entry || null);
    setShowPopup(true);
  }

  // Editar a partir do histórico: fecha o histórico e abre o formulário
  function editFromHistory(entry) {
    setHistoryKey(null);
    openPopup(entry);
  }

  const actions = (
    <>
      {current && (
        <CustomButton
          variant="outlined"
          style="secondary"
          label={report.exporting ? 'Gerando...' : 'PDF'}
          icon={<PictureAsPdfOutlinedIcon />}
          enabled={!report.exporting}
          onClick={report.start}
        />
      )}
      {enabled && (ownEntry ? (
        <CustomButton
          variant="outlined"
          style="primary"
          label="Editar avaliação"
          icon={<IconEdit />}
          onClick={() => openPopup(ownEntry)}
        />
      ) : (
        <CustomButton
          variant="contained"
          style="primary"
          label="Nova avaliação"
          icon={<IconAdd />}
          onClick={() => openPopup(null)}
        />
      ))}
    </>
  );

  const otherAssessments = sorted.filter((item) => current && String(item.id) !== String(current.id));

  return (
    <>
      <ModuleCard
        accent="green"
        title="Medidas corporais"
        icon={<StraightenOutlinedIcon />}
        description="Peso, altura, circunferências e dobras com evolução"
        actions={actions}
        collapsible={collapsible}
      >
        <div className={styles.content}>
          {!current ? (
            enabled
              ? <BodyExampleOverview variant={variant} />
              : <ModuleEmptyState icon={<StraightenOutlinedIcon />} text="Nenhuma avaliação registrada." />
          ) : (
            <>
              <div className={styles.toolbar}>
                <div className={styles.context}>
                  <DateChip label={formatDate(current.measuredAt)} />
                  <span className={parts.muted}>
                    {sorted.length === 1 ? 'Única avaliação' : `Avaliação mais recente de ${sorted.length}`}
                    {reference && ` · variação em relação a ${formatDate(reference.measuredAt)}`}
                  </span>
                </div>

                <div className={styles.controls}>
                  {otherAssessments.length > 0 && (
                    <FormControl size="small" sx={{ minWidth: 190 }}>
                      <InputLabel id="body-measurements-reference-label">Comparar com</InputLabel>
                      <Select
                        labelId="body-measurements-reference-label"
                        label="Comparar com"
                        value={referenceMode}
                        onChange={(e) => setReferenceMode(e.target.value)}
                      >
                        <MenuItem value={REFERENCE_PREVIOUS}>Avaliação anterior</MenuItem>
                        <MenuItem value={REFERENCE_FIRST}>Primeira avaliação</MenuItem>
                        {otherAssessments.map((item) => (
                          <MenuItem key={item.id} value={String(item.id)}>{formatDate(item.measuredAt)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={view}
                    onChange={(e, value) => value && setView(value)}
                  >
                    <ToggleButton value={VIEW_OVERVIEW} className={parts.toggleButton}>
                      <AccessibilityNewOutlinedIcon />
                      Visão geral
                    </ToggleButton>
                    <ToggleButton value={VIEW_TABLE} className={parts.toggleButton}>
                      <TableChartOutlinedIcon />
                      Tabela
                    </ToggleButton>
                  </ToggleButtonGroup>
                </div>
              </div>

              {view === VIEW_OVERVIEW ? (
                <>
                  <BodyOverview
                    history={history}
                    current={current}
                    reference={reference}
                    variant={variant}
                    birthday={birthday}
                    onSelect={setHistoryKey}
                  />
                  {current.notes && <div className={styles.notes}>{current.notes}</div>}
                </>
              ) : (
                <BodyMeasurementsTable history={history} subject={{ variant, birthday }} enabled={enabled} onEdit={openPopup} />
              )}
            </>
          )}
        </div>
      </ModuleCard>

      {report.job && (
        <HiddenReport>
          <BodyMeasurementsReportTemplate ref={report.ref} {...report.paperProps} {...report.job} />
        </HiddenReport>
      )}

      {historyKey && (
        <BodyMeasurementHistoryPopup
          history={history}
          measureKey={historyKey}
          enabled={enabled}
          onEdit={editFromHistory}
          OnHide={() => setHistoryKey(null)}
        />
      )}

      {showPopup && (
        <CreateBodyMeasurementsPopup
          patientId={patientId}
          appointmentId={appointmentId}
          entry={selectedEntry}
          history={history}
          variant={variant}
          birthday={birthday}
          OnHide={popupHideHandler}
        />
      )}
    </>
  );
};

export default BodyMeasurements;
